import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { Guid, PieceTicket, PieceType, UpdatePieceTicketRequest } from "@/api/types";
import { formatMoney } from "@/lib/format";
import { newIdempotencyKey, wasRejected } from "./BulkAnimalsDialog";
import { DialogShell } from "./DialogShell";
import "./PieceTicketDialog.css";

/**
 * ef-060-7 — Add / Edit piece ticket.
 *
 * - ADD: person + piece type + period end + units (typed, or pulled as the head count of the type's
 *   herd/group as of a date) + note. A pull fills the units and locks them (read-only): they are a
 *   count, not a typed number, and the ticket is saved as a livestock ticket on that date. Switching
 *   the piece type or the date afterwards discards the pull and the units, so a stale count can never
 *   ride along under a different type.
 * - EDIT (open tickets only): units (a livestock ticket's are read-only — the head count on its date),
 *   period end, note. The ticket keeps pricing at ITS OWN rate, which the preview reads off the ticket.
 * - ONE Idempotency-Key per open and a synchronous in-flight guard (a ref), so a double-clicked Save
 *   posts once. The key rotates only after a 4xx (nothing was created and the body is about to change).
 * - Errors: client checks and a server 400 both land in ONE `role="alert"` and mark the field
 *   `aria-invalid`; nothing typed is ever cleared.
 */

const UNITS_MAX = 1_000_000;
const NOTE_MAX = 500;

export interface PieceTicketPerson {
  id: Guid;
  name: string;
}

type FieldName = "person" | "type" | "units" | "periodEnd" | "asOf" | "note";

/** `YYYY-MM-DD` in UTC — the day the server reasons in (`PieceTicketEndpoints.Today`). */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Display-only preview of `round(units x rate, 2)` (half away from zero, as the server does). Integer
 * math so a figure like 1.005 does not drift; the server's number is the one that is stored and shown
 * on the row afterwards.
 */
export function previewAmount(units: number, rate: number): number {
  const product = Math.round(units * 100) * Math.round(rate * 10_000); // millionths of a dollar
  return Math.floor((product + 5_000) / 10_000) / 100;
}

/** A rate in full up to four decimals, never less than cents: "0.50", "0.0625". */
export function formatRate(rate: number): string {
  const fixed = rate.toFixed(4);
  return fixed.replace(/0{1,2}$/, "");
}

/** Maps the API's validation keys onto this form's fields. */
const SERVER_FIELD: Record<string, FieldName> = {
  employeeid: "person",
  piecetypeid: "type",
  units: "units",
  periodend: "periodEnd",
  asof: "asOf",
  note: "note",
};

export function PieceTicketDialog({
  ticket,
  people,
  types,
  onClose,
  onSaved,
  onStale,
}: {
  /** The open ticket being edited; omit to add. */
  ticket?: PieceTicket;
  /** Active people only. */
  people: PieceTicketPerson[];
  /** Current (non-archived) piece types only. */
  types: PieceType[];
  onClose: () => void;
  /** After a successful save (the caller refreshes its list; the dialog closes itself). */
  onSaved: (saved: PieceTicket) => void;
  /** The server said the ticket is no longer open (409): refresh the list, keep the message up. */
  onStale: () => void;
}) {
  const editing = ticket !== undefined;
  const idPrefix = useId();
  const errorId = `${idPrefix}-error`;
  const formId = useId();
  const savingRef = useRef(false);
  const keyRef = useRef(newIdempotencyKey());
  const pullSeq = useRef(0);

  const [personId, setPersonId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [periodEnd, setPeriodEnd] = useState(ticket?.periodEnd ?? todayIso());
  const [units, setUnits] = useState(ticket ? String(ticket.units) : "");
  const [asOf, setAsOf] = useState(ticket?.asOf ?? todayIso());
  const [note, setNote] = useState(ticket?.note ?? "");
  /** The pulled head count: a livestock ticket's units are this, locked. Null = typed by hand. */
  const [pulled, setPulled] = useState<{ asOf: string; count: number } | null>(
    ticket?.source === "livestock" && ticket.asOf ? { asOf: ticket.asOf, count: ticket.units } : null,
  );
  const [pulling, setPulling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [invalid, setInvalid] = useState<Partial<Record<FieldName, true>>>({});

  const selectedType = types.find((t) => t.id === typeId);
  const linked = selectedType !== undefined && (selectedType.herdId != null || selectedType.groupId != null);
  const unitsLocked = pulled !== null;
  const rate = editing ? ticket.rate : selectedType?.rate;
  const unitLabel = editing ? ticket.unit : (selectedType?.unit ?? "");
  const unitsNumber = units.trim() === "" ? NaN : Number(units);
  const amountText =
    rate !== undefined && Number.isFinite(unitsNumber) && unitsNumber > 0
      ? formatMoney(previewAmount(unitsNumber, rate))
      : null;

  function touch(field: FieldName): void {
    setInvalid((prev) => {
      if (!prev[field]) return prev;
      const rest = { ...prev };
      delete rest[field];
      return rest;
    });
  }

  function discardPull(): void {
    pullSeq.current += 1; // an in-flight pull answers into nothing
    setPulling(false);
    if (pulled !== null) {
      setPulled(null);
      setUnits("");
    }
  }

  function chooseType(next: string): void {
    setTypeId(next);
    touch("type");
    if (!editing) discardPull();
  }

  function chooseAsOf(next: string): void {
    setAsOf(next);
    touch("asOf");
    if (!editing) discardPull();
  }

  async function pullHeadCount(): Promise<void> {
    if (!selectedType || !linked || pulling) return;
    setMessages([]);
    if (asOf === "") {
      setInvalid((prev) => ({ ...prev, asOf: true }));
      setMessages(["Enter the date to take the head count as of."]);
      return;
    }
    if (asOf > todayIso()) {
      setInvalid((prev) => ({ ...prev, asOf: true }));
      setMessages(["The head count date cannot be in the future."]);
      return;
    }
    const seq = (pullSeq.current += 1);
    setPulling(true);
    try {
      const result = await demoApi.getLivestockHeadcount({
        ...(selectedType.herdId ? { herdId: selectedType.herdId } : { groupId: selectedType.groupId ?? undefined }),
        asOf,
      });
      if (seq !== pullSeq.current) return; // the type or date moved on while this was in flight
      setPulled({ asOf: result.asOf || asOf, count: result.count });
      setUnits(String(result.count));
      touch("units");
    } catch (err) {
      if (seq !== pullSeq.current) return;
      setMessages([problemMessage(err, "Could not read the head count.")]);
    } finally {
      if (seq === pullSeq.current) setPulling(false);
    }
  }

  /** @ui-check: mirrors the server's checks so the reason shows without a round trip. */
  function validate(): { problems: string[]; fields: Partial<Record<FieldName, true>> } {
    const problems: string[] = [];
    const fields: Partial<Record<FieldName, true>> = {};
    function fail(field: FieldName, message: string): void {
      fields[field] = true;
      problems.push(message);
    }
    if (!editing && personId === "") fail("person", "Choose a person.");
    if (!editing && typeId === "") fail("type", "Choose a piece type.");
    if (periodEnd === "") fail("periodEnd", "Enter the period end date.");
    else if (periodEnd > todayIso()) fail("periodEnd", "The period end cannot be in the future.");
    if (!unitsLocked) {
      if (units.trim() === "" || !Number.isFinite(unitsNumber)) fail("units", "Enter the units.");
      else if (unitsNumber <= 0) fail("units", "Units must be more than 0.");
      else if (unitsNumber > UNITS_MAX) fail("units", `Units cannot exceed ${UNITS_MAX.toLocaleString("en-US")}.`);
      else if (Math.abs(unitsNumber * 100 - Math.round(unitsNumber * 100)) > 1e-6) {
        fail("units", "Units can have at most 2 decimal places.");
      }
    } else if (pulled !== null && pulled.count <= 0) {
      fail("units", "There were no head on that date, so there is nothing to pay.");
    }
    if (note.length > NOTE_MAX) fail("note", `The note must be ${NOTE_MAX} characters or fewer.`);
    return { problems, fields };
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (savingRef.current) return; // synchronous: a double click never gets past here
    setMessages([]);
    setInvalid({});
    const { problems, fields } = validate();
    if (problems.length > 0) {
      setMessages(problems);
      setInvalid(fields);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const trimmedNote = note.trim();
      let saved: PieceTicket;
      if (editing) {
        const body: UpdatePieceTicketRequest = {};
        if (ticket.source === "manual" && unitsNumber !== ticket.units) body.units = unitsNumber;
        if (periodEnd !== ticket.periodEnd) body.periodEnd = periodEnd;
        if (trimmedNote !== (ticket.note ?? "")) body.note = trimmedNote;
        if (Object.keys(body).length === 0) {
          onClose(); // nothing changed — nothing to write
          return;
        }
        saved = await demoApi.updatePieceTicket(ticket.id, body);
      } else {
        saved = await demoApi.createPieceTicket(
          {
            employeeId: personId,
            pieceTypeId: typeId,
            periodEnd,
            ...(trimmedNote !== "" ? { note: trimmedNote } : {}),
            ...(pulled !== null
              ? { source: "livestock" as const, asOf: pulled.asOf }
              : { source: "manual" as const, units: unitsNumber }),
          },
          keyRef.current,
        );
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      if (wasRejected(err)) keyRef.current = newIdempotencyKey(); // nothing was created; the body is about to change
      if (err instanceof ApiError && err.status === 409) onStale();
      const serverFields = problemFieldErrors(err);
      if (serverFields) {
        const fields: Partial<Record<FieldName, true>> = {};
        const lines: string[] = [];
        for (const [key, list] of Object.entries(serverFields)) {
          const field = SERVER_FIELD[key.toLowerCase()];
          if (field) fields[field] = true;
          lines.push(...list);
        }
        setInvalid(fields);
        setMessages(lines.length > 0 ? lines : [problemMessage(err, "Could not save this ticket.")]);
      } else {
        setMessages([problemMessage(err, "Could not save this ticket.")]);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  const describedBy = messages.length > 0 ? errorId : undefined;
  const title = editing ? "Edit piece ticket" : "Add piece ticket";

  return (
    <DialogShell
      title={title}
      onClose={onClose}
      busy={saving}
      restoreFocus
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="piece-dialog__form"
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
        aria-busy={saving}
        data-state={saving ? "saving" : messages.length > 0 ? "error" : "ready"}
      >
        {editing ? (
          <p className="piece-dialog__summary">
            <strong>{ticket.employeeName}</strong> &middot; {ticket.pieceTypeName}
          </p>
        ) : (
          <>
            <div className="piece-dialog__field">
              <label htmlFor={`${idPrefix}-person`}>Person</label>
              <select
                id={`${idPrefix}-person`}
                value={personId}
                aria-invalid={invalid.person ? true : undefined}
                aria-describedby={invalid.person ? describedBy : undefined}
                onChange={(e) => {
                  setPersonId(e.target.value);
                  touch("person");
                }}
              >
                <option value="">Choose a person</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="piece-dialog__field">
              <label htmlFor={`${idPrefix}-type`}>Piece type</label>
              <select
                id={`${idPrefix}-type`}
                value={typeId}
                aria-invalid={invalid.type ? true : undefined}
                aria-describedby={invalid.type ? describedBy : undefined}
                onChange={(e) => chooseType(e.target.value)}
              >
                <option value="">Choose a piece type</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {selectedType && (
                <span className="piece-dialog__hint">
                  <span className="num">${formatRate(selectedType.rate)}</span> per {selectedType.unit}
                  {linked ? ` · head count from ${selectedType.herdName ?? selectedType.groupName ?? "its herd"}` : ""}
                </span>
              )}
            </div>
          </>
        )}

        <div className="piece-dialog__field">
          <label htmlFor={`${idPrefix}-period`}>Period ends</label>
          <input
            id={`${idPrefix}-period`}
            type="date"
            value={periodEnd}
            aria-invalid={invalid.periodEnd ? true : undefined}
            aria-describedby={invalid.periodEnd ? describedBy : undefined}
            onChange={(e) => {
              setPeriodEnd(e.target.value);
              touch("periodEnd");
            }}
          />
        </div>

        {!editing && (
          <fieldset className="piece-dialog__pull" disabled={saving}>
            <legend>Head count</legend>
            <div className="piece-dialog__pull-row">
              <div className="piece-dialog__field">
                <label htmlFor={`${idPrefix}-asof`}>Count as of</label>
                <input
                  id={`${idPrefix}-asof`}
                  type="date"
                  value={asOf}
                  disabled={!linked}
                  aria-invalid={invalid.asOf ? true : undefined}
                  aria-describedby={invalid.asOf ? describedBy : undefined}
                  onChange={(e) => chooseAsOf(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={!linked || pulling}
                aria-busy={pulling}
                onClick={() => void pullHeadCount()}
              >
                Pull head count
              </button>
              {pulled !== null && (
                <button type="button" className="btn btn--ghost" onClick={discardPull}>
                  Type units instead
                </button>
              )}
            </div>
            {!linked && (
              <span className="piece-dialog__hint">
                {typeId === ""
                  ? "Choose a piece type linked to a herd or group to pull its head count."
                  : "This piece type is not linked to a herd or group, so type the units."}
              </span>
            )}
          </fieldset>
        )}

        <div className="piece-dialog__field">
          <label htmlFor={`${idPrefix}-units`}>Units</label>
          <input
            id={`${idPrefix}-units`}
            className="num"
            type="number"
            inputMode="decimal"
            step="any"
            value={units}
            readOnly={unitsLocked}
            aria-invalid={invalid.units ? true : undefined}
            aria-describedby={invalid.units ? describedBy : undefined}
            onChange={(e) => {
              setUnits(e.target.value);
              touch("units");
            }}
          />
          {unitLabel !== "" && <span className="piece-dialog__hint">{unitLabel}</span>}
        </div>

        <p role="status" className="piece-dialog__pulled">
          {pulled !== null ? `Head count on ${pulled.asOf}: ${pulled.count}` : ""}
        </p>

        <p className="piece-dialog__amount" data-state={amountText !== null ? "ready" : "empty"}>
          Amount{" "}
          <strong className="num">{amountText ?? "—"}</strong>
          {rate !== undefined && (
            <span className="piece-dialog__hint">
              {" "}
              at <span className="num">${formatRate(rate)}</span> per {unitLabel || "unit"}
            </span>
          )}
        </p>

        <div className="piece-dialog__field">
          <label htmlFor={`${idPrefix}-note`}>Note</label>
          <input
            id={`${idPrefix}-note`}
            type="text"
            value={note}
            maxLength={NOTE_MAX}
            aria-invalid={invalid.note ? true : undefined}
            aria-describedby={invalid.note ? describedBy : undefined}
            onChange={(e) => {
              setNote(e.target.value);
              touch("note");
            }}
          />
        </div>

        {messages.length > 0 && (
          <div id={errorId} className="piece-dialog__error" role="alert">
            {messages.map((m) => (
              <p key={m}>{m}</p>
            ))}
          </div>
        )}

      </form>
    </DialogShell>
  );
}
